import { create } from 'zustand'

interface SelectionState {
  selectedItems: Set<string>
  lastSelectedIndex: number | null

  // Actions
  toggle: (key: string, index: number) => void
  selectRange: (keys: string[], startIndex: number) => void
  selectAll: (keys: string[]) => void
  clear: () => void
  cleanSelection: (validKeys: string[]) => void
  isSelected: (key: string) => boolean
  getSelectedCount: () => number
  getSelectedKeys: () => string[]
}

export const useSelectionStore = create<SelectionState>((set, get) => ({
  selectedItems: new Set<string>(),
  lastSelectedIndex: null,

  toggle: (key: string, index: number) => {
    set((state) => {
      const newSelected = new Set(state.selectedItems)
      if (newSelected.has(key)) {
        newSelected.delete(key)
      } else {
        newSelected.add(key)
      }
      return {
        selectedItems: newSelected,
        lastSelectedIndex: index
      }
    })
  },

  selectRange: (keys: string[], _startIndex: number) => {
    set((state) => {
      const newSelected = new Set(state.selectedItems)
      keys.forEach(key => newSelected.add(key))
      return {
        selectedItems: newSelected,
        // Don't update lastSelectedIndex to allow extending the range
        lastSelectedIndex: state.lastSelectedIndex
      }
    })
  },

  selectAll: (keys: string[]) => {
    set((state) => {
      const currentSize = state.selectedItems.size
      const totalSize = keys.length

      if (currentSize === totalSize) {
        // Deselect all
        return {
          selectedItems: new Set<string>(),
          lastSelectedIndex: null
        }
      } else {
        // Select all
        return {
          selectedItems: new Set(keys),
          lastSelectedIndex: null
        }
      }
    })
  },

  clear: () => {
    set({
      selectedItems: new Set<string>(),
      lastSelectedIndex: null
    })
  },

  cleanSelection: (validKeys: string[]) => {
    const validKeySet = new Set(validKeys)
    const currentSelected = get().selectedItems

    // Filter to keep only selections that exist in validKeys
    const cleanedSelected = new Set(
      Array.from(currentSelected).filter(key => validKeySet.has(key))
    )

    // Only update state if something changed
    if (cleanedSelected.size !== currentSelected.size) {
      set({ selectedItems: cleanedSelected })
    }
  },

  isSelected: (key: string) => {
    return get().selectedItems.has(key)
  },

  getSelectedCount: () => {
    return get().selectedItems.size
  },

  getSelectedKeys: () => {
    return Array.from(get().selectedItems)
  }
}))
